<template>
  <!-- Dynamic wrapper -->
  <component :is="wrapperComponent" class="investment-card" v-bind="wrapperAttrs">
    <!-- Header -->
    <div class="investment-card__header">
      <span class="investment-card__title" :style="{ backgroundColor: colors.primaryBadge, color: colors.primaryText }">
        {{ title || '' }}
      </span>
      <span class="investment-card__badge">
        {{ showComingSoon ? "Coming Soon" : badge }}
      </span>
    </div>

    <!-- Rate -->
    <p class="investment-card__rate">
      <span class="earn-upto-text" :style="{ color: colors.primaryText }">
        {{ earnText }}
      </span>
      <br />
      <span class="highlight" :style="{ color: colors.tertiaryText }">{{ rate }}</span>
      <span :style="{ color: colors.primaryText }">p.a.</span>
    </p>

    <!-- Features list -->
    <ul class="investment-card__features">
      <li v-for="(item, index) in features" :key="index" class="investment-card__feature">
        <ClientOnly>
          <v-icon :icon="item.icon" :style="{ color: colors.primaryText }" size="10" />
        </ClientOnly>
        <span :style="{ color: colors.primaryText }">{{ item.text }}</span>
      </li>
    </ul>

    <!-- Icon section -->
    <ul class="investment-card__icons" v-if="showIconSection">
      <li v-for="(item, index) in iconList" :key="index" class="investment-card__icon">
        <img :src="item.imgSrc" :alt="item.altText" />
      </li>
    </ul>

    <CommonSuccessBadge v-if="showSuccessBadge && succesBadgeText" :text="succesBadgeText" width="max-content" />

    <!-- Visual CTA container inside link -->
    <span class="investment-card__button" :style="{ backgroundColor: (isButtonDisable || showComingSoon) ? '#f3f4f6' : colors.buttonBg, color: (isButtonDisable || showComingSoon) ? '#6b7280' : '#fff' }" aria-hidden="true">
      {{ buttonText || '' }}
    </span>
  </component>
</template>

<script setup lang="ts">
import { useTheme } from "~/composables/useTheme";
import { computed, ref, onMounted } from "vue";
import { NuxtLink } from "#components"; // Import NuxtLink explicitly
import { useUtmTracking } from "~/composables/useUtmTracking";
import { useRoute } from "vue-router";

const { colors } = useTheme();
const { buildRedirectUrl } = useUtmTracking();
const route = useRoute();
const { investorBaseUrl, investorUrl } = useInvestorPlatform();

interface Props {
  title?: string;
  badge?: string;
  rate?: string;
  buttonText?: string;
  features?: Array<{ icon: string; text: string }>;
  isButtonDisable?: boolean;
  buttonRedirectionTo?: string;
  buttonRedirectionHref?: string;
  showComingSoon?: boolean;
  showSuccessBadge?: boolean;
  succesBadgeText?: string;
  showIconSection?: boolean;
  iconList?: Array<{ imgSrc: string; altText: string }>;
}

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  badge: undefined,
  rate: undefined,
  buttonText: undefined,
  features: undefined,
  isButtonDisable: false,
  buttonRedirectionTo: undefined,
  buttonRedirectionHref: undefined,
  showComingSoon: false,
  showSuccessBadge: false,
  succesBadgeText: undefined,
  showIconSection: false,
  iconList: undefined,
});

// Initialize URLs as refs with base values for SSR
const ctaName = computed(() => `start-${props.title?.toLowerCase().replaceAll(/\s+/g, '-') || 'plan'}-card`);

const earnText = computed(() => {
  if (props.title?.toLowerCase().includes("bond")) {
    return "Earn";
  }
  return "Earn upto";
});

const buttonHref = ref<string | undefined>(undefined);
const wrapperHref = ref<string | undefined>(undefined);

// Update URLs with UTM parameters on client side
onMounted(() => {
  if (props.buttonRedirectionHref) {
    const isInvestEquirize = props.buttonRedirectionHref.startsWith(investorBaseUrl.value);
    if (isInvestEquirize) {
      buttonHref.value = buildRedirectUrl(props.buttonRedirectionHref, route.path, ctaName.value);
      wrapperHref.value = buildRedirectUrl(props.buttonRedirectionHref, route.path, ctaName.value);
    } else {
      buttonHref.value = props.buttonRedirectionHref;
      wrapperHref.value = props.buttonRedirectionHref;
    }
  } else if (!props.buttonRedirectionTo) {
    const defaultUrl = buildRedirectUrl(investorUrl("/login"), route.path, ctaName.value);
    buttonHref.value = defaultUrl;
    wrapperHref.value = defaultUrl;
  }
});

// Button props
const buttonProps = computed(() => {
  if (props.buttonRedirectionHref) {
    const isInvestEquirize = props.buttonRedirectionHref.startsWith(investorBaseUrl.value);
    // Use ref value if available (client-side), otherwise use base URL (SSR)
    const href = buttonHref.value || props.buttonRedirectionHref;
    return {
      href: href,
      target: "_blank",
      class: isInvestEquirize ? "sign_up_initiated" : ""
    };
  }
  if (props.buttonRedirectionTo) return { to: props.buttonRedirectionTo };
  // Use ref value if available (client-side), otherwise use base URL (SSR)
  const href = buttonHref.value || investorUrl("/login");
  return { href: href, target: "_blank", class: "sign_up_initiated" };
});

// Wrapper logic
const wrapperComponent = computed(() =>
  props.buttonRedirectionTo ? NuxtLink : "a"
);

const wrapperAttrs = computed(() => {
  if (props.showComingSoon) return { style: "pointer-events:none; opacity:0.7" };
  if (props.buttonRedirectionTo) return { to: props.buttonRedirectionTo };
  if (props.buttonRedirectionHref) {
    const isInvestEquirize = props.buttonRedirectionHref.startsWith(investorBaseUrl.value);
    // Use ref value if available (client-side), otherwise use base URL (SSR)
    const href = wrapperHref.value || props.buttonRedirectionHref;
    return {
      href: href,
      target: "_blank",
      rel: isInvestEquirize ? "nofollow noopener noreferrer" : "noopener noreferrer",
      class: isInvestEquirize ? "sign_up_initiated" : ""
    };
  }
  // Use ref value if available (client-side), otherwise use base URL (SSR)
  const href = wrapperHref.value || investorUrl("/login");
  return { href: href, target: "_blank", rel: "nofollow noopener noreferrer", class: "sign_up_initiated" };
});
</script>


<style scoped>
.investment-card {
  cursor: pointer;
  border: 1px solid var(--secondaryText);
  border-radius: 16px;
  padding: 16px 20px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05);
  width: 96%;
  display: flex;
  flex-direction: column;
}

.investment-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.investment-card__title {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
}

.earn-upto-text {
  font-size: 17px;
}

.investment-card__badge {
  font-size: 14px;
  font-weight: 600;
}

.investment-card__rate {
  color: #666;
  font-size: 14px;
}

.investment-card__rate .highlight {
  font-weight: 700;
  font-size: 28px;
}

.investment-card__features {
  margin-top: 16px;
  padding-left: 0;
  list-style: none;
}

.investment-card__feature {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #222;
}

.investment-card__icons {
  display: flex;
  gap: 6px;
  margin: 16px 0px;
  padding-left: 0;
  list-style: none;
}

.investment-card__icon {
  display: flex;
  align-items: center;
}

.investment-card__icon>img {
  border-radius: 16px;
}

.investment-card__button {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40px;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 15px;
  font-weight: 500;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
  text-decoration: none;
  text-align: center;
}

@media (min-width: 1024px) {
  .investment-card {
    min-width: 430px;
    padding: 40px;
    width: auto;
  }

  .earn-upto-text {
    font-size: 24px;
  }

  .investment-card__rate .highlight {
    font-size: 36px;
  }
}
</style>
